import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import Button from '../shared/Button';
import Combobox from '../shared/Combobox';
import { getAllTransporters } from '../../services/firestore/transporterService';
import { getVehicles } from '../../services/firestore/vehicleService';
import { getTransporterDrivers } from '../../services/firestore/driverService';
import { getGeneralSettings } from '../../services/firestore/systemConfigService';

import HistoryLogModal from '../shared/HistoryLogModal';
import { VEHICLE_STATUS, STATUS_LABELS } from '../../constants/statuses';

const ReservationModal = ({ isOpen, onClose, onSave, onDelete, initialData, docks, tenantId, userData }) => {
    const [showHistory, setShowHistory] = useState(false);
    const [formData, setFormData] = useState({
        plateNumber: '',
        transporterName: '',
        transporterId: null,
        supplierCode: '',
        driverName: '',
        driverPhone: '',
        volume: '',
        weight: '',
        dockId: '',
        startTime: '09:00',
        bookingDate: dayjs().format('YYYY-MM-DD'),
        duration: 60,
        status: 'pending',
        vehicleType: '',
        vehicleTypeName: '',
        note: ''
    });

    const [transporters, setTransporters] = useState([]);
    const [myVehicles, setMyVehicles] = useState([]);
    const [myDrivers, setMyDrivers] = useState([]);
    const [weightUnit, setWeightUnit] = useState('kg');

    useEffect(() => {
        if (isOpen && tenantId) {
            fetchTransporters();
            fetchSettings();
            if (userData?.role === 'transporter') {
                fetchMyResources();
            }
        }
    }, [isOpen, tenantId, userData]);

    const fetchSettings = async () => {
        try {
            const settings = await getGeneralSettings(tenantId);
            if (settings?.weightUnit) setWeightUnit(settings.weightUnit);
        } catch (error) {
            console.error('Failed to load settings', error);
        }
    };

    const fetchTransporters = async () => {
        try {
            const data = await getAllTransporters(tenantId, true);
            setTransporters(data);
        } catch (error) {
            console.error('Failed to load transporters', error);
        }
    };

    const fetchMyResources = async () => {
        if (!userData?.transporterId || !tenantId) return;
        try {
            const [vData, dData] = await Promise.all([
                getVehicles({ tenantId, transporterId: userData.transporterId }),
                getTransporterDrivers(userData.transporterId, tenantId)
            ]);
            setMyVehicles(vData);
            setMyDrivers(dData);
        } catch (error) {
            console.error('Failed to load transporter resources', error);
        }
    };

    useEffect(() => {
        if (initialData) {
            // Convert Date objects to time strings for the form
            // Safety check for startTime/endTime
            const startStr = initialData.startTime instanceof Date ? initialData.startTime : new Date();
            const endStr = initialData.endTime instanceof Date ? initialData.endTime : new Date(startStr.getTime() + 3600000);

            const startHours = startStr.getHours().toString().padStart(2, '0');
            const startMinutes = startStr.getMinutes().toString().padStart(2, '0');

            const durationMs = endStr - startStr;
            const durationMins = Math.round(durationMs / 60000);

            setFormData({
                plateNumber: initialData.plateNumber || '',
                transporterName: initialData.transporterName || '',
                transporterId: initialData.transporterId || null,
                driverName: initialData.driverName || '',
                driverPhone: initialData.driverPhone || '',
                dockId: initialData.dockId || docks[0]?.id || '',
                startTime: `${startHours}:${startMinutes}`,
                bookingDate: dayjs(startStr).format('YYYY-MM-DD'),
                duration: durationMins,
                status: initialData.status || 'pending',
                vehicleType: initialData.vehicleType || '',
                vehicleTypeName: initialData.vehicleTypeName || '',
                volume: initialData.metrics?.volume || initialData.volume || initialData.plannedVolume || '',
                weight: initialData.metrics?.weight || initialData.weight || initialData.plannedWeight || '',
                supplierCode: initialData.supplierCode || initialData.metrics?.supplierCode || '',
                note: ''
            });
        } else {
            // Default new
            setFormData({
                plateNumber: '',
                transporterName: userData?.role === 'transporter' ? userData.transporterName : '',
                transporterId: userData?.role === 'transporter' ? userData.transporterId : null,
                supplierCode: userData?.role === 'transporter' ? userData.supplierCode : '',
                driverName: '',
                driverPhone: '',
                volume: '',
                weight: '',
                dockId: docks[0]?.id || '',
                startTime: '09:00',
                bookingDate: dayjs().format('YYYY-MM-DD'),
                duration: 60,
                status: 'pending',
                vehicleType: '',
                vehicleTypeName: '',
                note: ''
            });
        }
    }, [initialData, docks, isOpen]);

    // Check if reservation is in progress or completed
    const isInProgress = initialData && [
        VEHICLE_STATUS.FACILITY_ENTERED,
        VEHICLE_STATUS.IN_PARKING,
        VEHICLE_STATUS.LOADING_STARTED,
        VEHICLE_STATUS.LOADING_COMPLETED,
        VEHICLE_STATUS.FACILITY_EXITED,
        'arrived', 'loading', 'completed'
    ].includes(initialData.status);

    // Check if time or duration has changed from initial
    const isTimeChanged = useMemo(() => {
        if (!initialData) return false;
        const startStr = initialData.startTime instanceof Date ? initialData.startTime : new Date(initialData.startTime);
        const startH = startStr.getHours().toString().padStart(2, '0');
        const startM = startStr.getMinutes().toString().padStart(2, '0');
        const initialTimeStr = `${startH}:${startM}`;

        const durationMs = (initialData.endTime ? new Date(initialData.endTime) : new Date()) - (initialData.startTime ? new Date(initialData.startTime) : new Date());
        const durationMins = Math.round(durationMs / 60000);

        const initialDateStr = dayjs(startStr).format('YYYY-MM-DD');

        return formData.startTime !== initialTimeStr || formData.duration !== durationMins || formData.bookingDate !== initialDateStr;
    }, [formData.startTime, formData.duration, formData.bookingDate, initialData]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleTransporterChange = (item) => {
        if (!item) return;
        setFormData({
            ...formData,
            transporterName: item.companyName,
            transporterId: item.id,
            supplierCode: item.supplierCode
        });
    };

    const handleTransporterInput = (val) => {
        setFormData({
            ...formData,
            transporterName: val,
            transporterId: null // Reset ID if typing custom
        });
    };

    return (
        <>
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.7)',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(5px)'
            }}>
                <div style={{
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    padding: '24px',
                    borderRadius: '12px',
                    width: '500px',
                    border: '1px solid var(--border-color)',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <div className="flex justify-between items-center mb-lg">
                        <div className="flex flex-col">
                            <h3 className="text-lg font-bold">{initialData ? 'Rezervasyon Düzenle' : 'Yeni Rezervasyon'}</h3>
                            {initialData?.reservationNumber && (
                                <span className="text-xs font-mono text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded w-fit mt-1">
                                    {initialData.reservationNumber}
                                </span>
                            )}
                        </div>
                        {initialData && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowHistory(true)}
                                style={{ fontSize: '12px', padding: '4px 8px' }}
                            >
                                🕒 Geçmiş
                            </Button>
                        )}
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="flex gap-md">
                            <div className="form-group flex-1">
                                <label className="form-label flex justify-between items-center">
                                    <span>Plaka</span>
                                    {formData.vehicleTypeName && (
                                        <span style={{ fontSize: '10px', color: 'var(--primary-color)', background: 'var(--primary-color-20)', padding: '2px 6px', borderRadius: '4px' }}>
                                            Tip: {formData.vehicleTypeName}
                                        </span>
                                    )}
                                </label>
                                {userData?.role === 'transporter' ? (
                                    <Combobox
                                        items={formData.vehicleType
                                            ? myVehicles.filter(v => v.vehicleTypeId === formData.vehicleType)
                                            : myVehicles
                                        }
                                        value={formData.plateNumber}
                                        onChange={(item) => {
                                            if (!item) return;
                                            setFormData({
                                                ...formData,
                                                plateNumber: item.licensePlate,
                                                driverName: item.driver?.name || formData.driverName,
                                                driverPhone: item.driver?.phone || formData.driverPhone
                                            });
                                        }}
                                        onInputChange={(val) => setFormData({ ...formData, plateNumber: val.toUpperCase() })}
                                        placeholder="Plaka Seç..."
                                        labelKey="licensePlate"
                                        valueKey="id"
                                    />
                                ) : (
                                    <input
                                        className="input"
                                        required
                                        value={formData.plateNumber}
                                        onChange={(e) => setFormData({ ...formData, plateNumber: e.target.value.toUpperCase() })}
                                        placeholder="34 ABC 123"
                                    />
                                )}
                            </div>
                            <div className="form-group flex-1">
                                <label className="form-label">Statü</label>
                                <select
                                    className="input"
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    disabled={userData?.role === 'transporter'}
                                    style={userData?.role === 'transporter' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                                >
                                    {Object.entries(STATUS_LABELS)
                                        .filter(([key]) => !['rejected', 'confirmed', 'transporter_confirmed', 'pending', 'entered', 'parking', 'loading', 'completed', 'exited', 'loading_start', 'loading_end', 'waiting_reservation'].includes(key))
                                        .map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))
                                    }
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Firma</label>
                            {userData?.role === 'transporter' || initialData ? (
                                <input
                                    className="input"
                                    value={userData?.transporterName || formData.transporterName}
                                    disabled
                                    style={{ opacity: 0.7, cursor: 'not-allowed' }}
                                />
                            ) : (
                                <Combobox
                                    items={transporters}
                                    value={formData.transporterName}
                                    onChange={handleTransporterChange}
                                    onInputChange={handleTransporterInput}
                                    placeholder="Firma Ara veya Yaz..."
                                    labelKey="companyName"
                                    valueKey="id"
                                />
                            )}
                        </div>

                        <div className="flex gap-md">
                            <div className="form-group flex-1">
                                <label className="form-label">Sürücü Adı</label>
                                {userData?.role === 'transporter' ? (
                                    <Combobox
                                        items={myDrivers}
                                        value={formData.driverName}
                                        onChange={(item) => {
                                            if (!item) return;
                                            setFormData({ ...formData, driverName: item.name, driverPhone: item.phone || formData.driverPhone });
                                        }}
                                        onInputChange={(val) => setFormData({ ...formData, driverName: val })}
                                        placeholder="Sürücü Seç..."
                                        labelKey="name"
                                        valueKey="id"
                                    />
                                ) : (
                                    <input
                                        className="input"
                                        value={formData.driverName}
                                        onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                                        placeholder="Ad Soyad"
                                    />
                                )}
                            </div>
                            <div className="form-group flex-1">
                                <label className="form-label">Telefon</label>
                                <input
                                    className="input"
                                    value={formData.driverPhone}
                                    onChange={(e) => setFormData({ ...formData, driverPhone: e.target.value })}
                                    placeholder="05XX..."
                                    disabled={userData?.role === 'transporter'}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Rampa</label>
                            <select
                                className="input"
                                value={formData.dockId}
                                onChange={(e) => setFormData({ ...formData, dockId: e.target.value })}
                                disabled={userData?.role === 'transporter'}
                                style={userData?.role === 'transporter' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                            >
                                {docks.map(dock => (
                                    <option key={dock.id} value={dock.id}>{dock.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex gap-md">
                            <div className="form-group flex-1">
                                <label className="form-label">Hacim (m³)</label>
                                <input
                                    type="number"
                                    className="input"
                                    value={formData.volume}
                                    onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                                    placeholder="0"
                                    disabled={userData?.role === 'transporter'}
                                    style={userData?.role === 'transporter' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                                />
                            </div>
                            <div className="form-group flex-1">
                                <label className="form-label">Ağırlık ({weightUnit})</label>
                                <input
                                    type="number"
                                    className="input"
                                    value={formData.weight}
                                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                                    placeholder="0"
                                    disabled={userData?.role === 'transporter'}
                                    style={userData?.role === 'transporter' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                                />
                            </div>
                        </div>

                        <div className="flex gap-md">
                            <div className="form-group flex-1">
                                <label className="form-label">Saat</label>
                                <input
                                    type="time"
                                    className="input"
                                    value={formData.startTime}
                                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                                    disabled={isInProgress || userData?.role === 'transporter'}
                                    style={isInProgress || userData?.role === 'transporter' ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                                />
                            </div>
                            <div className="form-group flex-1">
                                <label className="form-label">Süre</label>
                                <select
                                    className="input"
                                    value={formData.duration}
                                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                                    style={{ width: '100%', ...(isInProgress || userData?.role === 'transporter' ? { opacity: 0.7, cursor: 'not-allowed' } : {}) }}
                                    disabled={isInProgress || userData?.role === 'transporter'}
                                >
                                    {[...Array(12)].map((_, i) => {
                                        const val = (i + 1) * 20;
                                        return <option key={val} value={val}>{val} dk</option>;
                                    })}
                                    {initialData && (formData.duration % 20 !== 0) && (
                                        <option value={formData.duration}>
                                            {formData.duration} dk
                                        </option>
                                    )}
                                </select>
                            </div>
                        </div>

                        <div className="form-group mb-md">
                            <label className="form-label">Planlanan Tarih</label>
                            <input
                                type="date"
                                className="input"
                                value={formData.bookingDate}
                                onChange={(e) => setFormData({ ...formData, bookingDate: e.target.value })}
                                min={userData?.role === 'transporter' ? dayjs().format('YYYY-MM-DD') : undefined}
                                style={isInProgress ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
                                disabled={isInProgress}
                            />
                            {userData?.role === 'transporter' && (
                                <p className="text-[10px] text-secondary mt-1">
                                    * Tarih değişikliği koordinatör onayına düşecektir.
                                </p>
                            )}
                        </div>


                        <div className="form-group mb-lg">
                            <label className="form-label">Not / Bilgilendirme</label>
                            <textarea
                                className="input"
                                value={formData.note}
                                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                placeholder={userData?.role === 'transporter' ? 'Koordinatöre iletmek istediğiniz notu yazınız (Örn: Saat değişikliği talebi)' : 'Rezervasyon notu...'}
                                rows={2}
                                style={{ resize: 'none' }}
                            />
                        </div>

                        <div className="flex justify-end gap-md mt-xl">
                            {onDelete && initialData && (
                                <Button type="button" variant="danger" onClick={() => onDelete(initialData.id)} style={{ marginRight: 'auto' }}>Sil</Button>
                            )}
                            <Button type="button" variant="secondary" onClick={onClose}>İptal</Button>

                            {userData?.role === 'transporter' && initialData ? (
                                <>
                                    <Button
                                        type="button"
                                        variant={isTimeChanged ? "primary" : "outline"}
                                        onClick={() => onSave(formData, false)}
                                        disabled={isInProgress}
                                    >
                                        {isTimeChanged ? 'Revize İste (Koordinatör Onayına Düşer)' : 'Güncelle'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="primary"
                                        onClick={() => onSave(formData, true)}
                                        className="shadow-lg shadow-primary/20"
                                        disabled={initialData.status === VEHICLE_STATUS.APPROVED || isTimeChanged || isInProgress}
                                        style={(initialData.status === VEHICLE_STATUS.APPROVED || isTimeChanged || isInProgress) ? { opacity: 0.6 } : {}}
                                    >
                                        {initialData.status === VEHICLE_STATUS.APPROVED ? 'Onaylandı' : 'Onayla'}
                                    </Button>
                                </>
                            ) : (
                                <Button type="submit" variant="primary">
                                    Kaydet
                                </Button>
                            )}
                        </div>
                    </form>
                </div >
            </div >

            <HistoryLogModal
                isOpen={showHistory}
                onClose={() => setShowHistory(false)}
                entityId={initialData?.id}
                tenantId={tenantId}
                title={`${initialData?.reservationNumber || initialData?.referenceNumber || initialData?.docNo || initialData?.documentNumber || ''}${initialData?.reservationNumber || initialData?.referenceNumber || initialData?.docNo || initialData?.documentNumber ? ' / ' : ''}${initialData?.plateNumber || ''} - Geçmiş Hareketler`}
                baseData={initialData}
            />
        </>
    );
};

export default ReservationModal;
